import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { motion, AnimatePresence } from "framer-motion";
import { X, Maximize2, Minimize2 } from "lucide-react";
import { imgSrc, fmtPrice, availabilityLabel } from "../lib/api";
import PayPalPay from "./PayPalPay";
import EnquiryForm from "./EnquiryForm";

export default function Lightbox({ artwork, contact, onClose }) {
  const [full, setFull] = useState(false);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") (full ? setFull(false) : onClose());
    };
    window.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [onClose, full]);

  const rows = [
    ["Medium", artwork.medium],
    ["Dimensions", artwork.dimensions],
    ["Year", artwork.year],
  ].filter(([, v]) => !!v);

  const fullView = (
    <AnimatePresence>
      {full && (
        <motion.div
          data-testid="fullscreen-image-view"
          className="fixed inset-0 z-[95] bg-ink flex flex-col"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.35 }}
          onClick={() => setFull(false)}
        >
          <div
            className="flex-1 min-h-0 flex items-center justify-center p-4 md:p-10 relative"
            onClick={(e) => e.stopPropagation()}
          >
            {!loaded && (
              <p className="absolute font-meta text-[10px] uppercase tracking-[0.35em] text-mute animate-pulse" data-testid="fullscreen-loading">
                Unveiling the work…
              </p>
            )}
            <motion.img
              key={artwork.id}
              src={imgSrc(artwork.image_path)}
              alt={artwork.title}
              onLoad={() => setLoaded(true)}
              initial={{ scale: 0.96, opacity: 0 }}
              animate={{ scale: 1, opacity: loaded ? 1 : 0 }}
              transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
              className="max-h-full max-w-full object-contain"
              data-testid="fullscreen-image"
            />
          </div>
          <div
            className="border-t border-line px-6 py-4 flex items-center justify-between gap-4"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="min-w-0">
              <p className="font-display italic text-lg truncate" data-testid="fullscreen-title">
                {artwork.title}
              </p>
              <p className="font-meta text-[9px] uppercase tracking-[0.25em] text-mute mt-1">
                {artwork.price ? fmtPrice(artwork.price) : "Enquire"} · {availabilityLabel(artwork.availability)}
              </p>
            </div>
            <div className="flex items-center gap-3 shrink-0">
              {contact && (
                <a
                  href={`mailto:${contact}`}
                  data-testid="fullscreen-enquire-link"
                  className="btn-metal px-5 py-2.5 font-meta text-[10px] uppercase tracking-[0.25em] text-ink"
                >
                  Enquire
                </a>
              )}
              <button
                onClick={() => setFull(false)}
                data-testid="fullscreen-image-close-button"
                className="flex items-center gap-2 border border-ochre/60 bg-ink/90 backdrop-blur px-6 py-3.5 font-meta text-[11px] uppercase tracking-[0.25em] text-ochre hover:bg-ochre hover:text-ink transition-colors duration-300"
              >
                Back <Minimize2 size={14} />
              </button>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );

  return (
    <motion.div
      data-testid="artwork-lightbox"
      className="fixed inset-0 z-[80] flex items-center justify-center p-4 md:p-10 bg-ink/90 backdrop-blur-sm"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.35 }}
      onClick={onClose}
    >
      <motion.div
        onClick={(e) => e.stopPropagation()}
        initial={{ y: 48, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        exit={{ y: 24, opacity: 0 }}
        transition={{ duration: 0.55, ease: [0.16, 1, 0.3, 1] }}
        className="relative w-full max-w-5xl bg-panel border border-line grid md:grid-cols-[1.35fr_1fr] max-h-[90vh] overflow-y-auto"
        data-testid="lightbox-panel"
      >
        <div
          className="bg-ink/60 flex items-center justify-center relative group cursor-zoom-in"
          onClick={() => setFull(true)}
          data-testid="lightbox-image-frame"
        >
          <img
            src={imgSrc(artwork.image_path)}
            alt={artwork.title}
            className="w-full h-72 md:h-full object-cover"
            data-testid="lightbox-image"
          />
          <span className="absolute bottom-3 right-3 flex items-center gap-2 font-meta text-[9px] uppercase tracking-[0.25em] bg-ink/80 backdrop-blur px-3 py-1.5 text-bone/90">
            <Maximize2 size={10} /> Full image
          </span>
        </div>
        <div className="p-6 md:p-10 flex flex-col">
          <p
            className={`font-meta text-[10px] uppercase tracking-[0.3em] ${
              artwork.sold ? "text-oxblood bg-oxblood/20 border border-oxblood/50 inline-block px-3 py-1.5" : artwork.availability === "prints" ? "text-ochre" : "text-gold"
            }`}
            data-testid="lightbox-availability"
          >
            {artwork.sold ? "Sold — this piece has found its home" : availabilityLabel(artwork.availability)}
          </p>
          <h3 className="font-display text-3xl md:text-4xl mt-4 leading-tight" data-testid="lightbox-title">
            {artwork.title}
          </h3>
          <div className="mt-8 border-t border-line">
            {rows.map(([k, v]) => (
              <div key={k} className="flex justify-between items-baseline gap-6 py-3 border-b border-line">
                <span className="font-meta text-[10px] uppercase tracking-[0.25em] text-mute">{k}</span>
                <span className="text-sm text-right">{v}</span>
              </div>
            ))}
            <div className="flex justify-between items-baseline gap-6 py-3 border-b border-line">
              <span className="font-meta text-[10px] uppercase tracking-[0.25em] text-mute">Price</span>
              <span className="font-meta text-lg text-gold" data-testid="lightbox-price">
                {artwork.price ? fmtPrice(artwork.price) : "Enquire"}
              </span>
            </div>
          </div>
          <p className="mt-8 text-sm leading-relaxed text-mute">
            To enquire about this work, write to the studio — the artist replies personally.
          </p>
          {contact && (
            <a
              href={`mailto:${contact}`}
              data-testid="lightbox-enquire-link"
              className="btn-metal mt-5 inline-flex w-fit items-center px-6 py-3 font-meta text-[10px] uppercase tracking-[0.3em] text-ink"
            >
              Enquire
            </a>
          )}
          <button
            onClick={() => setFull(true)}
            data-testid="lightbox-image-view-button"
            className="mt-4 inline-flex w-fit items-center gap-2 font-meta text-[10px] uppercase tracking-[0.3em] text-mute hover:text-ochre transition-colors duration-300"
          >
            <Maximize2 size={12} /> View full-length image
          </button>
          <PayPalPay artwork={artwork} />
          <EnquiryForm artwork={artwork} enabled={!!contact} />
          <button
            onClick={onClose}
            data-testid="lightbox-close-button"
            className="sticky top-4 ml-auto mr-4 flex items-center gap-2 border border-ochre/60 bg-ink/90 backdrop-blur px-6 py-3.5 font-meta text-[11px] uppercase tracking-[0.25em] text-ochre hover:bg-ochre hover:text-ink transition-colors duration-300 shadow-lg shadow-black/40"
            style={{ position: "absolute", top: "1rem", right: "1rem", zIndex: 5 }}
          >
            Close <X size={16} />
          </button>
        </div>
      </motion.div>

      {createPortal(fullView, document.body)}
    </motion.div>
  );
}
